import {
  patchPositionPhoto,
  Position,
  positionFieldName,
  WorkOrder,
} from "@/services/backend/htz/work-order/types/work-order";
import { isImmutable } from "@/services/backend/documents/lib/document-state";
import { useEffect, useState } from "react";
import { ScrollArea, ScrollBar } from "@/shared/components/ui/scroll-area";
import { Button } from "@/shared/components/ui/button";
import { AlertCircle, PlusCircle, RefreshCw, Trash } from "lucide-react";
import { v4 } from "uuid";
import { Card } from "@/shared/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import t from "@/lang/lang";
import { cn } from "@/shared/lib/utils";
import { Photo } from "@/services/backend/htz/inspection/types/photo";
import { H4 } from "@/shared/components/typography";
import { Separator } from "@/shared/components/ui/separator";
import { Label } from "@/shared/components/ui/label";
import { Input } from "@/shared/components/ui/input";
import { TextareaField } from "@/shared/components/field/textarea-field";
import { FieldErrors } from "@/shared/components/field/field";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  PatchPhotoRequest,
  updateHtzWorkOrderPositionCache,
  useHtzWorkOrderAddAssessmentMutation,
  useHtzWorkOrderPatchPhotoMutation,
  useHtzWorkOrderPhotoDocumentationDeleteImageMutation,
  useHtzWorkOrderPhotoDocumentationShowImageQuery,
  useHtzWorkOrderPhotoDocumentationUploadImageMutation,
} from "@/services/backend/htz/work-order/api/work-order-api";
import { useToast } from "@/shared/hooks/use-toast";
import { parseRTKQueryError } from "@/shared/components/errors/parse-r-t-k-query-error";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import imageCompression from "browser-image-compression";
import {
  countFieldErrors,
  useFieldErrors,
  useErrValidationFailedContext,
} from "@/shared/lib/errors/err-validation-failed-context";
import { TableCard } from "@/shared/components/tables/table-card";
import { ComponentData } from "@/services/backend/htz/ahu/types/air-handling-unit-data";
import { useOptimisticDebouncedMutation } from "@/shared/lib/debounce/use-optimistic-debounced-mutation";
import { RemoveAssessmentDialog } from "@/routes/processes/[processId]/htz/work-orders/[workOrderId]/positions/_components/remove-assessment-dialog";

export function PhotoDocumentation({
  workOrder,
  position,
  activeComponent,
}: {
  workOrder: WorkOrder;
  position: Position;
  activeComponent: ComponentData | null;
}) {
  const [selectedPhotoId, setSelectedPhotoId] = useState<string | null>(null);
  const photo = position.photos.find(
    (p) =>
      p.id === selectedPhotoId &&
      p.componentId === activeComponent?.componentId,
  );

  return (
    <ScrollArea className="h-[65vh] space-y-2">
      <PhotoTable
        workOrder={workOrder}
        position={position}
        activeComponent={activeComponent}
        selectedPhotoId={selectedPhotoId}
        onSelectedPhotoIdChange={setSelectedPhotoId}
      />
      <div className="p-2 py-4">
        {photo && (
          <PhotoForm workOrder={workOrder} position={position} photo={photo} />
        )}
      </div>
      <ScrollBar orientation="vertical" />
    </ScrollArea>
  );
}

export function AddPhotoButton({
  workOrder,
  position,
  activeComponent,
}: {
  workOrder: WorkOrder;
  position: Position;
  activeComponent: ComponentData | null;
}) {
  const [add, { error, isLoading, reset }] =
    useHtzWorkOrderAddAssessmentMutation();
  const { toast } = useToast();
  useEffect(() => {
    if (error) {
      toast({
        ...parseRTKQueryError(error),
        variant: "destructive",
      });
      reset();
    }
  }, [error, reset, toast]);

  const { resetField } = useErrValidationFailedContext();
  const errorKey = positionFieldName(position, "photos");

  const doAdd = () => {
    if (isLoading || !activeComponent) return;

    add({
      id: position.workOrderId,
      positionId: position.id,
      type: "photo",
      assessmentId: v4(),
      componentId: activeComponent.componentId,
    });
    resetField(errorKey);
  };

  const disabled =
    isImmutable(workOrder) || activeComponent === null || isLoading;

  return (
    <Button onClick={doAdd} disabled={disabled}>
      <PlusCircle />
    </Button>
  );
}

function PhotoTable({
  workOrder,
  position,
  activeComponent,
  selectedPhotoId,
  onSelectedPhotoIdChange,
}: {
  workOrder: WorkOrder;
  position: Position;
  activeComponent: ComponentData | null;
  selectedPhotoId: string | null;
  onSelectedPhotoIdChange: (id: string | null) => void;
}) {
  const toggle = (photo: Photo) => {
    if (photo.id === selectedPhotoId) {
      onSelectedPhotoIdChange(null);
    } else {
      onSelectedPhotoIdChange(photo.id);
    }
  };

  const { fieldErrors } = useErrValidationFailedContext();

  return (
    <TableCard className="shadow-none">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="h-8">{t("Kommentar")}</TableHead>
            <TableHead className="h-8">{t("Foto ?")}</TableHead>
            <TableHead className="h-8">{t("")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {position.photos
            .filter((p) => p.componentId === activeComponent?.componentId)
            .map((photo) => (
              <TableRow
                key={photo.id}
                className={cn(
                  "cursor-pointer",
                  photo.id === selectedPhotoId ? "bg-accent" : "",
                )}
              >
                <TableCell className="py-1">
                  <span className="flex items-center space-x-2">
                    {countFieldErrors(
                      fieldErrors,
                      positionFieldName(position, "photos", photo.id),
                    ) > 0 && <AlertCircle className="h-6 w-6 text-red-500" />}
                  </span>
                  {photo.comment}
                </TableCell>
                <TableCell
                  className="w-full py-1"
                  onClick={() => toggle(photo)}
                >
                  {photo.fileId === null ? t("fehlt") : t("ja")}
                </TableCell>
                <TableCell className="py-1 text-right">
                  <RemoveAssessmentDialog
                    position={position}
                    assessmentId={photo.id}
                    title={t("Foto wirklich löschen?")}
                    description={t("Das Foto wird endgültig gelöscht")}
                    onRemoved={() => onSelectedPhotoIdChange(null)}
                    disabled={isImmutable(workOrder)}
                  />
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </TableCard>
  );
}

function PhotoForm({
  workOrder,
  position,
  photo,
}: {
  workOrder: WorkOrder;
  position: Position;
  photo: Photo;
}) {
  const request: PatchPhotoRequest = {
    id: position.workOrderId,
    positionId: position.id,
    assessmentId: photo.id,
  };
  const [set] = useOptimisticDebouncedMutation(
    request,
    updateHtzWorkOrderPositionCache,
    useHtzWorkOrderPatchPhotoMutation(),
  );

  return (
    <div className="h-full">
      <H4>{t("Foto")}</H4>
      <Separator className="mt-1 mb-4" />
      <div className="grid gap-4">
        <TextareaField
          label={t("Bemerkung")}
          value={photo.comment}
          onChange={(e) =>
            set(
              { ...request, comment: e.target.value },
              patchPositionPhoto(position, {
                ...photo,
                comment: e.target.value,
              }),
            )
          }
          fieldName={positionFieldName(position, "photos", photo.id, "comment")}
          disabled={isImmutable(workOrder)}
        />
        {photo.fileId === null && (
          <PhotoUpload
            workOrder={workOrder}
            position={position}
            photo={photo}
          />
        )}
        <PhotoDisplay workOrder={workOrder} position={position} photo={photo} />
      </div>
    </div>
  );
}

// PhotoUpload uploads the image of the photo. The upload sets the file of
// the photo, the refetched position shows it.
function PhotoUpload({
  workOrder,
  position,
  photo,
}: {
  workOrder: WorkOrder;
  position: Position;
  photo: Photo;
}) {
  const { toast } = useToast();
  const [
    upload,
    {
      isLoading: photoUploadIsLoading,
      error: photoUploadError,
      isSuccess: photoUploadIsSuccess,
      reset: photoUploadReset,
    },
  ] = useHtzWorkOrderPhotoDocumentationUploadImageMutation();

  useEffect(() => {
    if (photoUploadError) {
      toast({
        ...parseRTKQueryError(photoUploadError),
        variant: "destructive",
      });
      photoUploadReset();
    }
  }, [photoUploadError, photoUploadReset, toast]);

  const {
    errors: fileErrors,
    invalid: fileInvalid,
    reset: resetFileErrors,
  } = useFieldErrors(positionFieldName(position, "photos", photo.id, "fileId"));

  useEffect(() => {
    if (photoUploadIsSuccess) {
      resetFileErrors();
      toast({
        title: t("Foto hochgeladen"),
        description: t("Das Foto wurde erfolgreich hochgeladen."),
        variant: "success",
      });
      photoUploadReset();
    }
  }, [photoUploadIsSuccess, photoUploadReset, resetFileErrors, toast]);

  const disabled =
    photo.fileId !== null ||
    isImmutable(workOrder) ||
    // posIsLoading ||
    photoUploadIsLoading;

  return (
    <div className="grid gap-1.5">
      <Label
        className={cn(
          "ring-offset-background focus-visible:ring-ring inline-flex w-full items-center justify-center rounded-md text-sm font-medium whitespace-nowrap transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:pointer-events-none disabled:opacity-50",
          "border-input bg-background hover:bg-accent hover:text-accent-foreground border",
          "h-9 rounded-md px-3",
          fileInvalid && "border-destructive",
        )}
      >
        <Input
          type="file"
          className="hidden"
          accept="image/jpeg,image/png"
          onChange={async (e) => {
            if (e.target.files && e.target.files[0]) {
              // compress image
              const options = {
                maxSizeMB: 0.3,
                maxWidthOrHeight: 1920,
                useWebWorker: true,
              };
              try {
                const compressedFile = await imageCompression(
                  e.target.files[0],
                  options,
                );
                const id = v4();
                // upload compressed image
                upload({
                  id: workOrder.id,
                  positionId: position.id,
                  photoId: photo.id,
                  fileId: id,
                  file: compressedFile,
                });
                e.target.value = "";
                e.target.files = null;
              } catch (err) {
                const parse = parseRTKQueryError(err as FetchBaseQueryError);
                toast({
                  title: parse?.title,
                  description: parse?.description,
                  variant: "destructive",
                });
              }
            }
          }}
          disabled={disabled}
        />
        {photoUploadIsLoading && (
          <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
        )}
        <span>{t("Foto hinzufügen")}</span>
      </Label>
      <FieldErrors errors={fileErrors} />
    </div>
  );
}

function PhotoDisplay({
  workOrder,
  position,
  photo,
}: {
  workOrder: WorkOrder;
  position: Position;
  photo: Photo;
}) {
  const {
    data: image,
    isLoading,
    error,
  } = useHtzWorkOrderPhotoDocumentationShowImageQuery(
    {
      id: workOrder.id,
      fileId: photo.fileId!,
    },
    {
      skip: !photo.fileId,
    },
  );

  if (photo.fileId === null) {
    return null;
  }

  if (isLoading) {
    return (
      <Card className="text-muted-foreground animate-pulse p-6">
        {t("Lade Daten...")}
      </Card>
    );
  }

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  return (
    <div className="relative">
      <img
        className="w-full rounded-lg"
        src={image!.objectURL}
        alt={photo.comment}
      />
      <DeleteImageDialog
        workOrder={workOrder}
        position={position}
        photo={photo}
      />
    </div>
  );
}

// DeleteImageDialog deletes the image of the photo. The deletion removes
// the file of the photo, the refetched position shows it.
function DeleteImageDialog({
  workOrder,
  position,
  photo,
}: {
  workOrder: WorkOrder;
  position: Position;
  photo: Photo;
}) {
  const [open, setOpen] = useState(false);
  const [deleteImage, { isLoading, error, isSuccess, reset }] =
    useHtzWorkOrderPhotoDocumentationDeleteImageMutation();

  useEffect(() => {
    if (isSuccess) {
      reset();
      setOpen(false);
    }
  }, [isSuccess, reset]);

  const onClick = () => {
    if (isLoading || photo.fileId === null) {
      return;
    }

    deleteImage({
      id: workOrder.id,
      positionId: position.id,
      photoId: photo.id,
      fileId: photo.fileId,
    });
  };

  const disabled = photo.fileId === null || isImmutable(workOrder) || isLoading;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild disabled={disabled}>
        <Button className="absolute top-4 right-4" variant="destructive">
          <Trash className="h-5 w-5" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogTitle>{t("Foto wirklich löschen?")}</DialogTitle>
        <RTKQueryErrorAlert error={error} />
        <DialogFooter>
          <Button onClick={onClick} variant="destructive">
            {t("Löschen")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
